import { Component, Element, Event, h, Prop, Watch, EventEmitter, Method, Listen, Host } from '@stencil/core';

import { KEYBOARD } from '../../../../config/constants';
import { getTextContent } from '../../../../services/utils';

/**
 * @part content - HTML `<div>` container that holds the content of the item inside the component's shadow DOM
 * @part prefix - HTML `<div>` container that holds the prefix content (e.g: icon) inside the component's shadow DOM
 * @part suffix - HTML `<div>` container that holds the suffix content (e.g: icon) inside the component's shadow DOM
 */
@Component({
  tag: 'pk-menu-item',
  styleUrl: './scss/pk-menu-item.scss',
  shadow: {
    delegatesFocus: true,
  },
})
export class PkMenuItem {
  // Own Properties
  // ====================

  private ariaLabel: string;
  private mainElem: HTMLElement;
  private mainContentElem: HTMLDivElement;
  private submenuElem: HTMLDivElement;
  private MENU_ITEM_SELECTOR = 'pk-menu-item';
  private MENU_SUBITEM_SELECTOR = 'pk-menu-subitem';

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkMenuItemElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** If true, the menu item will be shown as active/focus. */
  @Prop({ reflect: true }) active: boolean = false;

  /** If true, the menu item will be shown in a disabled state. */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** If true, the submenu panel will be expanded (valid only if `hasSubmenu = "true"`) */
  @Prop({ reflect: true, mutable: true }) expanded: boolean = false;

  /** If true, the item will behave as having subitems for secondary-level navigation  */
  @Prop({ reflect: true, mutable: true }) hasSubmenu: boolean = false;

  /** This number represents the index of the menu sub item  */
  @Prop({ reflect: true }) level: number = 1;

  /**
   * @internal
   * If true, the border left of the item will be highlighted when selected
   */
  @Prop({ reflect: true }) highlight: boolean = false;

  /** If provided, the menu-item will be treated as a link and it set the URL that the hyperlink points to. */
  @Prop({ reflect: true }) href: string;

  /**
   * Specifies where to display the linked URL, as the name for a browsing context (a tab, window, or <iframe>).
   * Details: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target
   */
  @Prop({ reflect: true }) linkTarget?: string = '_self';

  /** Specifies the relationship between the current page and the linked page */
  @Prop({ reflect: true }) linkRel?: string;

  /** If true, the menu item will be shown as selected. */
  @Prop({ reflect: true }) selected: boolean = false;

  /**
   * Where to display the linked URL, as the name for a browsing context (a tab, window, or iframe).
   * Details: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target
   */
  @Prop({ reflect: true }) target: string;

  /** A unique value to store in the menu item. */
  @Prop({ reflect: true }) value: string;

  // Prop lifecycle events
  // =======================

  @Watch('highlight')
  handleSubmenuHighlightBorder() {
    if (!this.hasSubmenu) return;

    const menuSubitems = this.getSubItems();
    if (!menuSubitems.length) return;

    menuSubitems.forEach((item: HTMLPkMenuSubitemElement | HTMLPkMenuItemElement) => (item.highlight = this.highlight));
  }

  @Watch('expanded')
  handleExpanded() {
    if (!this.hasSubmenu) return;

    this.pkExpand.emit(this.expanded);

    const menuSubitems = this.getSubItems(true);
    if (!menuSubitems.length) return;

    menuSubitems.forEach((item: HTMLPkMenuSubitemElement | HTMLPkMenuItemElement) => (item.disabled = !this.expanded));
  }

  @Watch('level')
  handleLevels() {
    if (!this.hasSubmenu) return;
    const menuSubitems = this.getSubItems();
    if (!menuSubitems.length) return;

    menuSubitems.forEach((item) => {
      item.level = this.level + 1;
    });
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the item is expanded */
  @Event() pkExpand: EventEmitter;

  /** Callback handler to be called when the item is focused */
  @Event() pkFocus: EventEmitter;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================
  componentDidLoad() {
    this.handleAriaLabel();
    this.handleSubmenuHighlightBorder();
    this.handleLevels();

    /**
     * Expand sub-menu panel on component load
     * if any of its subItems is currently active/selected
     */
    this.handleInitialExpandedState();
  }

  // Listeners
  // ==============

  @Listen('pkFocus')
  handleFocusEvent(e: CustomEvent) {
    const item = this.getSubItems().find((i) => i === e.target);
    if (!item) return;

    this.setActiveSubItem(item);
  }

  @Listen('keydown')
  handleKeyDownEvent(e: KeyboardEvent) {
    if (e.target !== this.el) return;

    this.handleKeyDown(e);
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  /** Apply focus to the menu item */
  @Method()
  async focusMenuItem() {
    this.mainElem.focus();
  }

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private handleClick = (ev: Event) => {
    if (!this.hasSubmenu || this.isLink) return;
    ev.preventDefault();

    this.toggleExpanded();
  };

  private toggleExpanded = (expanded = !this.expanded) => {
    if (!this.hasSubmenu) return;

    this.handleAutoCollapse();

    this.expanded = expanded;
    if (!this.expanded) {
      const subItems = this.getSubItems();
      subItems
        .filter((el) => el.tagName.toLowerCase() === this.MENU_ITEM_SELECTOR)
        .forEach((item: HTMLPkMenuItemElement) => (item.expanded = false));
    }
  };

  private handleEnterOrSpace = () => (this.isLink ? this.mainElem.click() : this.toggleExpanded());

  private handleKeyDown = (ev: KeyboardEvent) => {
    const keyMap = {
      [KEYBOARD.Enter]: this.handleEnterOrSpace,
      [KEYBOARD.Space]: this.handleEnterOrSpace,
      [KEYBOARD.ArrowRight]: () => this.toggleExpanded(true),
      [KEYBOARD.ArrowLeft]: () => this.toggleExpanded(false),
    } as const;

    let action = keyMap[ev.key];
    if (action) {
      ev.preventDefault();
      action();
      return;
    }

    const items = this.getSubItems();
    if (!items.length) return;

    const selectedItem = this.getActiveSubItem();
    let index = items.indexOf(selectedItem);
    const lastIndex = items.length - 1;

    const keyActionsMap = {
      [KEYBOARD.ArrowDown]: (i: number) => i + 1,
      [KEYBOARD.ArrowUp]: (i: number) => i - 1,
    };

    action = keyActionsMap[ev.key];
    if (!action) return;

    ev.preventDefault();
    ev.stopPropagation();

    index = action(index);

    if (index < 0) index = lastIndex;
    if (index > lastIndex) index = 0;

    const itemToSelect = items[index];
    if (!itemToSelect) return;

    if (itemToSelect.tagName.toLowerCase() === this.MENU_ITEM_SELECTOR) {
      (itemToSelect as HTMLPkMenuItemElement).focusMenuItem();
    } else if (itemToSelect.tagName.toLowerCase() === this.MENU_SUBITEM_SELECTOR) {
      (itemToSelect as HTMLPkMenuSubitemElement).focusMenuSubitem();
    }
  };

  private handleFocus = () => {
    this.pkFocus.emit();
  };

  private handleAriaLabel = () => {
    const slot = this.mainContentElem?.querySelector('slot') ?? null;
    if (!slot) return;

    this.ariaLabel = getTextContent(slot);
  };

  private get isLink(): boolean {
    return !!this.href && !this.hasSubmenu;
  }

  private getSubItems = (includeDisabled = false): (HTMLPkMenuSubitemElement | HTMLPkMenuItemElement)[] => {
    if (!this.submenuElem) return [];

    const slot = this.submenuElem.querySelector('slot');
    return [...slot.assignedElements({ flatten: true })].filter(
      (el: HTMLPkMenuItemElement | HTMLPkMenuSubitemElement) =>
        [this.MENU_ITEM_SELECTOR, this.MENU_SUBITEM_SELECTOR].includes(el.tagName.toLowerCase()) &&
        (includeDisabled || !el.disabled),
    ) as [HTMLPkMenuItemElement | HTMLPkMenuSubitemElement];
  };

  private getActiveSubItem = () => {
    return this.getSubItems().find((i) => i.active);
  };

  private setActiveSubItem = (item?: HTMLPkMenuItemElement | HTMLPkMenuSubitemElement) => {
    this.getSubItems().forEach((i) => (i.active = i === item));
  };

  private onSubMenuSlotChange = () => {
    this.hasSubmenu = !!this.getSubItems().length;
  };

  private handleInitialExpandedState = () => {
    if (!this.hasSubmenu) return;

    const subItems = this.getSubItems(true);
    if (!subItems.length) return;

    const expanded = subItems.some((subItem: HTMLPkMenuSubitemElement | HTMLPkMenuItemElement) => subItem.selected);
    subItems.forEach((subItem: HTMLPkMenuSubitemElement | HTMLPkMenuItemElement) => (subItem.disabled = !expanded));

    this.expanded = expanded;
  };

  private getPkMenu = () => this.el.closest<HTMLPkMenuElement>('pk-menu');

  private getPreviousExpandedItem = (pkMenu: HTMLPkMenuElement) => {
    if (!pkMenu) return;
    return Array.from(pkMenu.querySelectorAll('pk-menu-item')).find(
      (item) => item !== this.el && item !== this.el.parentElement && item.expanded,
    );
  };

  private handleAutoCollapse = () => {
    const pkMenu = this.getPkMenu();
    if (!pkMenu || pkMenu.multiExpand) return;

    const prevExpandedItem = this.getPreviousExpandedItem(pkMenu);
    if (prevExpandedItem) {
      prevExpandedItem.expanded = false;
    }
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const TagElem = this.isLink ? 'a' : 'button';
    const iconName = this.el.level > 1 ? 'navigation-dropdown-indicator' : 'navigation-expand';

    return (
      <Host>
        <TagElem
          ref={(el) => (this.mainElem = el)}
          class={{
            'pk-menu__item': true,
            [`level-${this.level}`]: true,
            active: this.active,
            selected: this.selected,
            disabled: this.disabled,
            highlight: this.highlight,
            expanded: this.expanded,
            'has-border': this.el.level > 1 && this.expanded,
          }}
          href={this.isLink ? this.href : undefined}
          rel={this.isLink ? this.linkRel : undefined}
          target={this.isLink ? this.linkTarget : undefined}
          aria-label={this.ariaLabel}
          aria-disabled={this.disabled ? 'true' : 'false'}
          aria-current={this.selected ? 'true' : 'false'}
          tabIndex={this.disabled ? -1 : undefined}
          onClick={this.handleClick}
          onFocus={this.handleFocus}
          role="menuitem"
        >
          {/* Prefix slot */}
          <div part="prefix" class="pk-menu__item--prefix flex flex-none items-center">
            <slot name="prefix" />
          </div>
          {/* Main content slot */}
          <div
            part="content"
            class="flex-auto overflow-hidden text-ellipsis whitespace-nowrap"
            ref={(div) => (this.mainContentElem = div)}
          >
            <slot />
          </div>
          {/* Suffix slot */}
          <div part="suffix" class="pk-menu__item--suffix flex flex-none items-center">
            {this.hasSubmenu ? (
              <pk-icon class="pk-menu__item--navigation-icon" name={iconName}></pk-icon>
            ) : (
              <slot name="suffix" />
            )}
          </div>
        </TagElem>
        {/* Secondary menu level */}
        <div
          class={{
            'pk-menu__item--submenu': true,
            expanded: this.expanded,
            '!hidden': !this.hasSubmenu,
          }}
          ref={(div) => (this.submenuElem = div)}
        >
          <div class="overflow-hidden">
            <slot name="submenu" onSlotchange={this.onSubMenuSlotChange} />
          </div>
        </div>
      </Host>
    );
  }
}
